import { CombinedGraphQLErrors, type ErrorLike } from '@apollo/client';
import { GRAPHQL_NOT_FOUND_CLASSIFICATION } from '../constants/general';

// Extracts the specific, user-facing message(s) from a thrown error.
// CombinedGraphQLErrors carries the real per-field GraphQL messages, which the
// apollo errorLink attempts to clean (strips DGS preambles / internal-error
// noise — see apollo-client.helper.ts), so treat them as best-effort
// user-facing. Fall back to a plain Error message, then to String().
export function getDetailedErrorMessage(error: unknown): string {
  if (CombinedGraphQLErrors.is(error)) {
    const messages = error.errors
      .map((e) => e.message)
      .filter((m): m is string => Boolean(m));
    if (messages.length > 0) {
      return messages.join('\n');
    }
  }
  if (error instanceof Error) {
    return error.message;
  }
  return String(error);
}

/** The server has no such thing, so asking again can't help. */
export const isNotFoundError = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(
    ({ extensions }) =>
      extensions?.classification === GRAPHQL_NOT_FOUND_CLASSIFICATION
  );

/** Awaits a query from `client.query` or a lazy hook, rejecting on failure: the
 * app's `errorPolicy: 'all'` (ADR 0012) resolves it with `error` instead. */
export const dataOrThrow = async <TData>(
  pending: Promise<{ data?: TData; error?: ErrorLike }>
): Promise<TData> => {
  const { data, error } = await pending;
  if (error) {
    throw error;
  }
  if (data === undefined) {
    throw new Error('The query returned no data');
  }
  return data;
};

/** Like `dataOrThrow`, but keeps partial data: rejects only when the error
 * leaves `select` with nothing. */
export const selectOrThrow = async <TData, TValue>(
  pending: Promise<{ data?: TData; error?: ErrorLike }>,
  select: (data: TData) => TValue | null | undefined
): Promise<TValue | null> => {
  const { data, error } = await pending;
  const value = (data === undefined ? undefined : select(data)) ?? null;
  if (value === null && error) {
    throw error;
  }
  return value;
};
